/**
 * itemProfitability.ts — Pure computation of margin per SKU and per category:
 * which products actually make money, and which are being sold below cost.
 *
 * Rules (shared with the P&L builder, applied at line granularity):
 *
 *  1. **Revenue and COGS are both ex-GST.** A line's `taxable_amount` is the
 *     post-discount, pre-tax value; `cost_price` is the ex-GST unit cost. Using
 *     both keeps margin honest — tax is not profit.
 *  2. **Net of returns.** A credit-note line (sign −1) subtracts its quantity,
 *     revenue and COGS, so a returned item stops counting as a sale.
 *  3. **Missing cost is disclosed, not invented.** Lines with `costPrice == null`
 *     contribute revenue but no COGS, overstating margin; the share of lines and
 *     of revenue without a cost basis is reported so the screen can caveat it.
 *     A negative margin is still reliable — un-costed lines only *raise* margin,
 *     so anything that comes out negative is a genuine loss.
 *
 * Sorted by margin (not revenue): the point of the report is to surface what is
 * and isn't worth selling. Pure — see `itemProfitDataSource.ts` for the bridge.
 */

export const CREDIT_NOTE = 'CREDIT_NOTE';

export function signOf(invoiceType: string): 1 | -1 {
  return invoiceType === CREDIT_NOTE ? -1 : 1;
}

export function round2(n: number): number {
  return Math.round((n + Number.EPSILON) * 100) / 100;
}

const safeDivide = (num: number, den: number): number => (den > 0 ? num / den : 0);

// ─── Inputs ──────────────────────────────────────────────────────────────────

export interface ItemLineInput {
  productId: string;
  quantity: number;
  /** Ex-GST, post-discount line value — the revenue basis. */
  taxableAmount: number;
  /** Ex-GST unit cost snapshot; null = no cost basis. */
  costPrice: number | null;
  invoiceType: string;
}

export interface ItemProductInput {
  name: string;
  category: string;
}

export interface ItemProfitabilityInput {
  lines: ItemLineInput[];
  /** productId → name + category (already resolved, with fallbacks). */
  products: Map<string, ItemProductInput>;
}

// ─── Output ──────────────────────────────────────────────────────────────────

export interface ItemProfit {
  productId: string;
  name: string;
  category: string;
  quantity: number;
  revenue: number;
  cogs: number;
  margin: number;
  marginPct: number;
  lineCount: number;
  linesWithoutCost: number;
  /** No line on this product carries a cost basis — margin is meaningless. */
  costMissing: boolean;
}

export interface CategoryProfit {
  category: string;
  quantity: number;
  revenue: number;
  cogs: number;
  margin: number;
  marginPct: number;
}

export interface ItemProfitability {
  /** Every product with activity, sorted by margin desc. */
  items: ItemProfit[];
  /** Categories, sorted by margin desc. */
  categories: CategoryProfit[];
  /** Items sold below cost (negative margin), worst first. */
  belowCost: ItemProfit[];
  totals: {
    revenue: number;
    cogs: number;
    margin: number;
    marginPct: number;
  };
  lineCount: number;
  linesWithoutCost: number;
  linesWithoutCostShare: number;
  revenueWithoutCostShare: number;
}

interface Acc {
  quantity: number;
  revenue: number;
  cogs: number;
  lineCount: number;
  linesWithoutCost: number;
}

const emptyAcc = (): Acc => ({ quantity: 0, revenue: 0, cogs: 0, lineCount: 0, linesWithoutCost: 0 });

export function buildItemProfitability(input: ItemProfitabilityInput): ItemProfitability {
  const { lines, products } = input;

  const byProduct = new Map<string, Acc>();
  const byCategory = new Map<string, Acc>();

  let saleRevenue = 0; // ex-GST sale line revenue, for the coverage weight
  let saleRevenueNoCost = 0;

  for (const li of lines) {
    const sign = signOf(li.invoiceType);
    const revenue = sign * li.taxableAmount;
    const costed = li.costPrice != null;
    const cogs = costed ? sign * li.quantity * li.costPrice! : 0;

    const p = byProduct.get(li.productId) ?? emptyAcc();
    p.quantity += sign * li.quantity;
    p.revenue += revenue;
    p.cogs += cogs;
    p.lineCount += 1;
    if (!costed) p.linesWithoutCost += 1;
    byProduct.set(li.productId, p);

    const categoryName = products.get(li.productId)?.category ?? 'Uncategorised';
    const c = byCategory.get(categoryName) ?? emptyAcc();
    c.quantity += sign * li.quantity;
    c.revenue += revenue;
    c.cogs += cogs;
    c.lineCount += 1;
    if (!costed) c.linesWithoutCost += 1;
    byCategory.set(categoryName, c);

    if (li.invoiceType !== CREDIT_NOTE) {
      saleRevenue += li.taxableAmount;
      if (!costed) saleRevenueNoCost += li.taxableAmount;
    }
  }

  const items: ItemProfit[] = [...byProduct.entries()]
    .map(([productId, a]) => {
      const info = products.get(productId);
      const margin = a.revenue - a.cogs;
      return {
        productId,
        name: info?.name ?? 'Unknown product',
        category: info?.category ?? 'Uncategorised',
        quantity: round2(a.quantity),
        revenue: round2(a.revenue),
        cogs: round2(a.cogs),
        margin: round2(margin),
        marginPct: safeDivide(margin, a.revenue),
        lineCount: a.lineCount,
        linesWithoutCost: a.linesWithoutCost,
        costMissing: a.linesWithoutCost === a.lineCount,
      };
    })
    // A product whose activity nets to nothing (sold then fully returned) is noise.
    .filter((it) => it.quantity !== 0 || it.revenue !== 0)
    .sort((a, b) => b.margin - a.margin);

  const categories: CategoryProfit[] = [...byCategory.entries()]
    .map(([category, a]) => {
      const margin = a.revenue - a.cogs;
      return {
        category,
        quantity: round2(a.quantity),
        revenue: round2(a.revenue),
        cogs: round2(a.cogs),
        margin: round2(margin),
        marginPct: safeDivide(margin, a.revenue),
      };
    })
    .filter((c) => c.quantity !== 0 || c.revenue !== 0)
    .sort((a, b) => b.margin - a.margin);

  // Below cost: reliably a loss (un-costed lines only raise margin). Exclude
  // items with no cost basis at all — their "margin" is entirely revenue.
  const belowCost = items
    .filter((it) => it.margin < 0 && !it.costMissing)
    .sort((a, b) => a.margin - b.margin);

  const totalRevenue = items.reduce((s, it) => s + it.revenue, 0);
  const totalCogs = items.reduce((s, it) => s + it.cogs, 0);
  const totalMargin = totalRevenue - totalCogs;

  const lineCount = lines.length;
  const linesWithoutCost = lines.filter((li) => li.costPrice == null).length;

  return {
    items,
    categories,
    belowCost,
    totals: {
      revenue: round2(totalRevenue),
      cogs: round2(totalCogs),
      margin: round2(totalMargin),
      marginPct: safeDivide(totalMargin, totalRevenue),
    },
    lineCount,
    linesWithoutCost,
    linesWithoutCostShare: safeDivide(linesWithoutCost, lineCount),
    revenueWithoutCostShare: safeDivide(saleRevenueNoCost, saleRevenue),
  };
}
